import { useEffect, useMemo, useState, type FormEvent } from "react";
import { addDoc, collection, deleteDoc, doc, getDoc, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { Plus, Trash2 } from "lucide-react";
import { paths, type Estimate, type Invoice, type Job, type PricebookItem, type ProjectItem } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { textareaClass } from "./JobEditCard";

// Project items: a builder's bid options ("Elevation A"), each with its scope
// and priced lines. Picked on a lot's job or work order, so the job carries
// the scope and its invoice starts from these prices.

export type ProjectItemRow = ProjectItem & { id: string };
const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
const totalOf = (lines: Array<{ quantity: number; unitPriceCents: number }>) =>
  lines.reduce((s, l) => s + Math.round(l.quantity * l.unitPriceCents), 0);

export function useProjectItems(tenantId: string | null, projectId: string | null | undefined): ProjectItemRow[] {
  const [items, setItems] = useState<ProjectItemRow[]>([]);
  useEffect(() => {
    setItems([]);
    if (!tenantId || !projectId) return;
    return onSnapshot(
      collection(db, paths.tenantProjectItems(tenantId, projectId)),
      (snap) =>
        setItems(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as ProjectItem) }))
            .sort((a, b) => Number(a.status === "archived") - Number(b.status === "archived") || a.name.localeCompare(b.name, undefined, { numeric: true })),
        ),
      () => setItems([]),
    );
  }, [tenantId, projectId]);
  return items;
}

/** The copy a job keeps of its item (no costs; bid prices locked in). */
export function jobItemCopy(it: ProjectItemRow): NonNullable<Job["projectItem"]> {
  return { id: it.id, name: it.name, lines: it.lines.map((l) => ({ description: l.description, quantity: l.quantity, unitPriceCents: l.unitPriceCents })) };
}

/** Pick one of a project's items (awarded first; archived only if already chosen). */
export function ProjectItemSelect({
  items,
  value,
  onChange,
  className,
}: {
  items: ProjectItemRow[];
  value: string;
  onChange: (item: ProjectItemRow | null) => void;
  className?: string;
}) {
  const usable = items.filter((i) => i.status !== "archived" || i.id === value);
  if (usable.length === 0) return null;
  return (
    <Select className={className} value={value} onChange={(e) => onChange(usable.find((i) => i.id === e.target.value) ?? null)}>
      <option value="">— no item —</option>
      {usable.map((i) => (
        <option key={i.id} value={i.id}>
          {i.name}
          {i.status === "bid" ? " (bid)" : ""}
          {i.totalCents ? ` · ${money(i.totalCents)}` : ""}
        </option>
      ))}
    </Select>
  );
}

interface DraftLine {
  description: string;
  quantity: string;
  price: string;
}
const toDraft = (l: { description: string; quantity: number; unitPriceCents: number }): DraftLine => ({
  description: l.description,
  quantity: String(l.quantity),
  price: (l.unitPriceCents / 100).toFixed(2),
});

function ItemForm({
  tenantId,
  projectId,
  customerId,
  uid,
  existing,
  onDone,
}: {
  tenantId: string;
  projectId: string;
  customerId: string | null;
  uid: string;
  existing: ProjectItemRow | null;
  onDone: () => void;
}) {
  const [name, setName] = useState(existing?.name ?? "");
  const [status, setStatus] = useState<ProjectItem["status"]>(existing?.status ?? "bid");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [lines, setLines] = useState<DraftLine[]>(existing?.lines.length ? existing.lines.map(toDraft) : [{ description: "", quantity: "1", price: "" }]);
  const [estimateId, setEstimateId] = useState<string | null>(existing?.estimateId ?? null);
  const [pieceRate, setPieceRate] = useState(existing?.pieceRateCents ? (existing.pieceRateCents / 100).toFixed(2) : "");
  const [pricebook, setPricebook] = useState<Array<PricebookItem & { id: string }>>([]);
  const [estimates, setEstimates] = useState<Array<Estimate & { id: string }>>([]);
  const [pbSearch, setPbSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(
    () =>
      onSnapshot(collection(db, paths.tenantPricebook(tenantId)), (snap) =>
        setPricebook(snap.docs.map((d) => ({ id: d.id, ...(d.data() as PricebookItem) })).filter((p) => !p.archivedAt)),
      ),
    [tenantId],
  );
  useEffect(() => {
    if (!customerId) return;
    return onSnapshot(query(collection(db, paths.tenantEstimates(tenantId)), where("customerId", "==", customerId)), (snap) =>
      setEstimates(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as Estimate) }))
          .filter((e) => e.status !== "declined")
          .sort((a, b) => b.createdAt - a.createdAt),
      ),
    );
  }, [tenantId, customerId]);

  const parsed = lines
    .map((l) => ({ description: l.description.trim(), quantity: parseFloat(l.quantity || "0"), unitPriceCents: Math.round(parseFloat(l.price || "0") * 100) }))
    .filter((l) => l.description && l.quantity > 0);
  const set = (i: number, next: Partial<DraftLine>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...next } : l)));
  const pbMatches = pbSearch.trim()
    ? pricebook.filter((p) => `${p.name} ${p.category}`.toLowerCase().includes(pbSearch.toLowerCase())).slice(0, 8)
    : [];

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return setError("Give the item a name, e.g. Elevation A.");
    setBusy(true);
    setError("");
    try {
      const data = {
        name: name.trim(),
        description: description.trim(),
        lines: parsed,
        totalCents: totalOf(parsed),
        status,
        estimateId,
        ...(pieceRate.trim() ? { pieceRateCents: Math.max(0, Math.round(parseFloat(pieceRate) * 100)) } : { pieceRateCents: 0 }),
        updatedAt: Date.now(),
      };
      if (existing) await updateDoc(doc(db, paths.tenantProjectItem(tenantId, projectId, existing.id)), data);
      else {
        const item: ProjectItem = { ...data, createdByUid: uid, createdAt: Date.now() };
        await addDoc(collection(db, paths.tenantProjectItems(tenantId, projectId)), item);
      }
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} className="space-y-2 rounded-lg bg-cream/60 p-3">
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <Label>Name</Label>
          <Input value={name} placeholder="Elevation A" onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label>Status</Label>
          <Select value={status} onChange={(e) => setStatus(e.target.value as ProjectItem["status"])}>
            <option value="bid">Bid</option>
            <option value="awarded">Awarded</option>
            <option value="archived">Archived</option>
          </Select>
        </div>
      </div>
      <div className="w-56">
        <Label>Tech piece rate $ (per job, internal)</Label>
        <Input type="number" min="0" step="0.01" value={pieceRate} placeholder="e.g. 150.00" onChange={(e) => setPieceRate(e.target.value)} />
      </div>
      <div>
        <Label>Scope</Label>
        <textarea className={textareaClass} rows={2} value={description} placeholder="What this option includes" onChange={(e) => setDescription(e.target.value)} />
      </div>
      {estimates.length > 0 ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-56 flex-1">
            <Label>Copy lines from an estimate</Label>
            <Select
              value=""
              onChange={async (e) => {
                const est = estimates.find((x) => x.id === e.target.value);
                if (!est) return;
                setLines(est.lines.map((l) => toDraft({ description: l.description, quantity: l.quantity, unitPriceCents: l.unitPriceCents })));
                setEstimateId(est.id);
                if (!description.trim() && est.notes) setDescription(est.notes);
              }}
            >
              <option value="">Pick an estimate…</option>
              {estimates.map((est) => (
                <option key={est.id} value={est.id}>
                  #{est.number} · {money(est.totalCents)} · {est.status}
                </option>
              ))}
            </Select>
          </div>
          {estimateId ? <span className="pb-2 text-[11px] text-steel">Linked to estimate #{estimates.find((x) => x.id === estimateId)?.number ?? "…"}</span> : null}
        </div>
      ) : null}
      <div className="space-y-1">
        {lines.map((l, i) => (
          <div key={i} className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              {i === 0 ? <Label>Line</Label> : null}
              <Input value={l.description} onChange={(e) => set(i, { description: e.target.value })} placeholder="16x7 insulated door, install" />
            </div>
            <div className="w-16">
              {i === 0 ? <Label>Qty</Label> : null}
              <Input type="number" min="0" step="any" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
            </div>
            <div className="w-24">
              {i === 0 ? <Label>Price</Label> : null}
              <Input type="number" step="0.01" value={l.price} onChange={(e) => set(i, { price: e.target.value })} />
            </div>
            <button type="button" aria-label="Remove line" className="pb-2 text-steel hover:text-danger" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={() => setLines((ls) => [...ls, { description: "", quantity: "1", price: "" }])}>
          <Plus size={13} /> Line
        </Button>
        <div className="relative min-w-48 flex-1">
          <Input className="py-1 text-xs" placeholder="Add from the pricebook…" value={pbSearch} onChange={(e) => setPbSearch(e.target.value)} />
          {pbMatches.length > 0 ? (
            <div className="absolute left-0 right-0 top-full z-10 mt-1 rounded-lg border border-steel/30 bg-white shadow">
              {pbMatches.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="block w-full px-2 py-1 text-left text-xs hover:bg-cream"
                  onClick={() => {
                    setLines((ls) => [...ls.filter((x) => x.description.trim()), toDraft({ description: p.name, quantity: 1, unitPriceCents: p.priceCents })]);
                    setPbSearch("");
                  }}
                >
                  {p.name} · {money(p.priceCents)}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <span className="text-xs font-semibold text-ink">{money(totalOf(parsed))}</span>
      </div>
      <div className="flex items-center gap-2">
        <Button type="submit" className="px-3 py-1 text-xs" busy={busy}>
          {existing ? "Save item" : "Add item"}
        </Button>
        <Button type="button" variant="ghost" className="px-3 py-1 text-xs" onClick={onDone}>
          Cancel
        </Button>
        {error ? <span className="text-xs text-danger">{error}</span> : null}
      </div>
    </form>
  );
}

/** The project page's Items view: every option, and how far each has gone (jobs, done, billed). */
export function ProjectItemsPanel({
  tenantId,
  projectId,
  customerId,
  uid,
  canEdit,
  jobs,
  invoices,
  onOpenJob,
}: {
  tenantId: string;
  projectId: string;
  customerId: string | null;
  uid: string;
  canEdit: boolean;
  jobs: Array<Job & { id: string }>;
  invoices: Array<Invoice & { id: string }>;
  onOpenJob: (jobId: string) => void;
}) {
  const items = useProjectItems(tenantId, projectId);
  const [editing, setEditing] = useState<string | null>(null); // item id, or "new"
  const [open, setOpen] = useState<string | null>(null);
  const stats = useMemo(() => {
    const m = new Map<string, { jobs: Array<Job & { id: string }>; done: number; billed: number }>();
    for (const j of jobs) {
      const id = j.projectItem?.id;
      if (!id) continue;
      const s = m.get(id) ?? m.set(id, { jobs: [], done: 0, billed: 0 }).get(id)!;
      s.jobs.push(j);
      if (j.status === "done") s.done += 1;
    }
    const jobItem = new Map(jobs.filter((j) => j.projectItem?.id).map((j) => [j.id, j.projectItem!.id]));
    for (const inv of invoices) {
      const id = inv.jobId ? jobItem.get(inv.jobId) : undefined;
      if (id && inv.status !== "void") m.get(id)!.billed += inv.totalCents;
    }
    return m;
  }, [jobs, invoices]);

  return (
    <Card className="space-y-2">
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">Items & bids ({items.length})</CardTitle>
        {canEdit && editing !== "new" ? (
          <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setEditing("new")}>
            <Plus size={13} /> New item
          </Button>
        ) : null}
      </div>
      <p className="text-[11px] text-steel">
        A bid option such as "Elevation A". Pick it on a lot's job or work order and the job carries its scope, and the job's
        invoice starts from these prices.
      </p>
      {editing === "new" ? (
        <ItemForm tenantId={tenantId} projectId={projectId} customerId={customerId} uid={uid} existing={null} onDone={() => setEditing(null)} />
      ) : null}
      <div className="divide-y divide-steel/10">
        {items.map((it) => {
          const s = stats.get(it.id) ?? { jobs: [], done: 0, billed: 0 };
          const isOpen = open === it.id;
          if (editing === it.id) {
            return <ItemForm key={it.id} tenantId={tenantId} projectId={projectId} customerId={customerId} uid={uid} existing={it} onDone={() => setEditing(null)} />;
          }
          return (
            <div key={it.id} className="py-1.5">
              <button className="flex w-full items-center justify-between gap-2 text-left" onClick={() => setOpen(isOpen ? null : it.id)}>
                <span className="min-w-0">
                  <span className={cn("text-sm font-semibold", it.status === "archived" ? "text-steel line-through" : "text-ink")}>{it.name}</span>
                  <span
                    className={cn(
                      "ml-2 rounded-full px-1.5 py-0.5 text-[10px] uppercase",
                      it.status === "awarded" ? "bg-success/15 text-success" : it.status === "bid" ? "bg-steel/15 text-ink" : "bg-steel/10 text-steel",
                    )}
                  >
                    {it.status}
                  </span>
                </span>
                <span className="shrink-0 text-xs text-steel">
                  {money(it.totalCents)} · {s.jobs.length} job{s.jobs.length === 1 ? "" : "s"} · {s.done} done · {money(s.billed)} billed {isOpen ? "▾" : "▸"}
                </span>
              </button>
              {isOpen ? (
                <div className="mt-1 space-y-1 rounded-lg bg-cream/50 p-2 text-xs">
                  {it.description ? <p className="whitespace-pre-wrap text-ink">{it.description}</p> : null}
                  {it.lines.map((l, i) => (
                    <div key={i} className="flex justify-between gap-2 text-ink">
                      <span className="min-w-0 truncate">
                        {l.quantity} × {l.description}
                      </span>
                      <span className="shrink-0">{money(Math.round(l.quantity * l.unitPriceCents))}</span>
                    </div>
                  ))}
                  {s.jobs.length > 0 ? (
                    <div className="border-t border-steel/15 pt-1">
                      {s.jobs.slice(0, 30).map((j) => (
                        <button key={j.id} className="block w-full truncate text-left text-brandorange-dark hover:underline" onClick={() => onOpenJob(j.id)}>
                          {j.lot ? `${j.lot} · ` : ""}
                          {j.title} · {j.status.replace("_", " ")}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {canEdit ? (
                    <div className="flex gap-3 pt-1">
                      <button className="text-steel underline hover:text-ink" onClick={() => setEditing(it.id)}>
                        Edit
                      </button>
                      {s.jobs.length === 0 ? (
                        <button
                          className="text-steel underline hover:text-danger"
                          onClick={() => void deleteDoc(doc(db, paths.tenantProjectItem(tenantId, projectId, it.id)))}
                        >
                          Delete
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
        {items.length === 0 && editing !== "new" ? <p className="py-2 text-xs text-steel">No items yet.</p> : null}
      </div>
    </Card>
  );
}

/** Read one item once (e.g. to copy onto a job when only its id is known). */
export async function loadProjectItem(tenantId: string, projectId: string, itemId: string): Promise<ProjectItemRow | null> {
  const s = await getDoc(doc(db, paths.tenantProjectItem(tenantId, projectId, itemId)));
  return s.exists() ? ({ id: s.id, ...(s.data() as ProjectItem) } as ProjectItemRow) : null;
}
